import { CompanyLogo } from '@/common/components'
import { config as appConfig } from '@/core'
import { setupTenantByEmail } from '@/core/tenant'
import { useViewport } from '@/hooks'
import * as amplitude from '@amplitude/analytics-browser'
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined'
import MailOutlinedIcon from '@mui/icons-material/MailOutlined'
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
} from '@mui/material'
import * as React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Footer } from '../components/Footer'
import {
  ArrowForwardIconStyle,
  ButtonStyle,
  ContainerBoxStyle,
  FooterBoxStyle,
  FooterContainerStyle,
  FormControlStyle,
  FormWrapperBoxStyle,
  InputLabelStyle,
  InputStyle,
  LoginContainerStyle,
  LoginGridContainerStyle,
  LogoContainerBoxStyle,
  LogoOuterBoxStyle,
  LogoWrapperBoxStyle,
  MailIconStyle,
  ResponsiveLineBreak320OnlyStyle,
  ResponsiveLineBreakStyle,
  TypographyEmailStyle,
  TypographyH4Style,
  WelcomeTextStyle,
  WrapperBoxStyle,
  errorMixin,
} from './LoginStyle'
import { createTenantUrl, useSessionCheck } from './hooks/useSessionCheck'

const ResponsiveBr = (): JSX.Element => <Box component="br" sx={ResponsiveLineBreakStyle} />
const ResponsiveBr320 = (): JSX.Element => <Box component="br" sx={ResponsiveLineBreak320OnlyStyle} />

export const Login = (): JSX.Element => {
  const { t } = useTranslation('general')
  const appUrl = appConfig.app.host
  const [email, setEmail] = React.useState('')
  const [errors, setErrors] = React.useState('')
  const [sessionActive, setSessionActive] = React.useState<boolean>(true)
  const viewport = useViewport()
  const isMobileOrTablet = viewport === 'mobile' || viewport === 'tablet'

  const handleSubmit = async (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault()
    setErrors('')
    const tenant = await setupTenantByEmail(email)
    amplitude.setUserId(email)
    if ('subdomain' in tenant) {
      window.location.assign(createTenantUrl(tenant.subdomain, email, appUrl))
    } else if ('statusText' in tenant) {
      setErrors(tenant.statusText)
    }
  }

  useSessionCheck(appUrl, setSessionActive, isMobileOrTablet)

  if (sessionActive) {
    return <></>
  }

  return (
    <Box sx={WrapperBoxStyle}>
      <Box sx={{ ...ContainerBoxStyle, px: 0 }}>
        <Container maxWidth="sm" sx={LoginContainerStyle}>
          <Grid
            container
            direction="column"
            sx={[
              {
                justifyContent: 'center',
                alignItems: 'center',
              },
              ...(Array.isArray(LoginGridContainerStyle) ? LoginGridContainerStyle : [LoginGridContainerStyle]),
            ]}>
            <Box sx={LogoWrapperBoxStyle}>
              <Box sx={LogoOuterBoxStyle}>
                <Box sx={LogoContainerBoxStyle}>
                  <CompanyLogo width={200} />
                </Box>
              </Box>
              <Box sx={FormWrapperBoxStyle}>
                <Typography variant="h4" sx={TypographyH4Style}>
                  <Trans
                    i18nKey="login_welcome"
                    ns="general"
                    components={{
                      accent: <Box component="span" sx={WelcomeTextStyle} />,
                      br1: <ResponsiveBr />,
                      br2: <ResponsiveBr320 />,
                    }}
                  />
                </Typography>
                <Typography sx={TypographyEmailStyle}>{t('login_description_business_email')}</Typography>
                <form onSubmit={handleSubmit}>
                  <FormControl variant="outlined" error={!!errors} fullWidth sx={FormControlStyle(!!errors)}>
                    <InputLabel htmlFor="email" sx={InputLabelStyle} required>
                      {t('login_enter_email_label')}
                    </InputLabel>
                    <OutlinedInput
                      inputProps={{ 'data-testid': 'name-input' }}
                      id="email"
                      value={email}
                      sx={InputStyle}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('login_enter_email')}
                      css={errors && errorMixin}
                      error={!!errors}
                      startAdornment={
                        <InputAdornment position="start">
                          <MailOutlinedIcon fontSize="small" sx={MailIconStyle} />
                        </InputAdornment>
                      }
                    />
                    <FormHelperText data-testid="helperText">{errors ? errors : ''}</FormHelperText>
                  </FormControl>

                  <Button
                    onClick={handleSubmit}
                    endIcon={<ArrowForwardOutlinedIcon sx={ArrowForwardIconStyle} />}
                    sx={ButtonStyle}>
                    {t('login_continue')}
                  </Button>
                </form>
              </Box>
            </Box>
          </Grid>
        </Container>
      </Box>
      <Box sx={{ ...FooterBoxStyle, px: 0 }}>
        <Container maxWidth="sm" sx={FooterContainerStyle}>
          <Footer />
        </Container>
      </Box>
    </Box>
  )
}
